/**
 * useDropdownPosition
 * Posição de dropdowns em `position: fixed` (coords de viewport),
 * com clamp opcional para não sair da tela.
 */
import { useState, useCallback, RefObject } from 'react';

export interface DropdownPosition {
    top: number;
    left: number;
}

export interface UseDropdownPositionOptions {
    /** Espaçamento vertical em pixels (default: 8) */
    offsetY?: number;
    /** Espaçamento horizontal em pixels (default: 0) */
    offsetX?: number;
    /** Alinhar à direita do trigger (default: false) */
    alignRight?: boolean;
    /** Largura do painel — usada para clamp nas bordas da viewport */
    panelWidth?: number;
    /** Margem mínima das bordas (default: 16) */
    viewportPadding?: number;
}

export interface UseDropdownPositionReturn {
    position: DropdownPosition;
    calculatePosition: () => void;
}

export function useDropdownPosition(
    triggerRef: RefObject<HTMLElement | null>,
    options: UseDropdownPositionOptions = {}
): UseDropdownPositionReturn {
    const {
        offsetY = 8,
        offsetX = 0,
        alignRight = false,
        panelWidth,
        viewportPadding = 16,
    } = options;

    const [position, setPosition] = useState<DropdownPosition>({ top: 0, left: 0 });

    const calculatePosition = useCallback(() => {
        if (!triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const top = rect.bottom + offsetY;

        const width =
            panelWidth ??
            Math.min(320, Math.max(0, window.innerWidth - viewportPadding * 2));

        let left = alignRight ? rect.right + offsetX - width : rect.left + offsetX;

        const maxLeft = window.innerWidth - width - viewportPadding;
        left = Math.min(Math.max(left, viewportPadding), Math.max(viewportPadding, maxLeft));

        setPosition({ top, left });
    }, [triggerRef, offsetY, offsetX, alignRight, panelWidth, viewportPadding]);

    return { position, calculatePosition };
}

export default useDropdownPosition;
